import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      // Harmonate Lime as primary color
      main: '#00D41F',
      light: '#4AE54A',
      dark: '#00A018',
      contrastText: '#ffffff',
    },
    secondary: {
      // Harmonate Sky as secondary color
      main: '#00D6FF',
      light: '#4AE5FF',
      dark: '#00A5CC',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F0F5F7', // Harmonate Cloud background
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#000000',
    },
    success: {
      main: '#00D41F', // Using Lime for success
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
    },
    info: {
      main: '#00D6FF', // Using Sky for info
    },
    // Harmonate brand colors
    harmonate: {
      lime: '#00D41F',
      sky: '#00D6FF',
      cloud: '#F0F5F7',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Varela Round", "Helvetica", "Arial", sans-serif',
    // Heading styles - Standard MUI sizes with Harmonate styling
    h1: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 700,
      fontSize: '3rem', // 48px
      lineHeight: 1.167,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 700,
      fontSize: '2.25rem', // 36px
      lineHeight: 1.2,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 700,
      fontSize: '1.875rem', // 30px
      lineHeight: 1.167,
      letterSpacing: '0em',
    },
    h4: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 700,
      fontSize: '1.5rem', // 24px
      lineHeight: 1.235,
      letterSpacing: '0.00735em',
    },
    h5: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 700,
      fontSize: '1.25rem', // 20px
      lineHeight: 1.334,
      letterSpacing: '0em',
    },
    h6: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 700,
      fontSize: '1.125rem', // 18px
      lineHeight: 1.6,
      letterSpacing: '0.0075em',
    },
    // Sub heading - using Varela Round as per brand guide
    subtitle1: {
      fontFamily: '"Varela Round", sans-serif',
      fontWeight: 700,
      fontSize: '1rem', // 16px
      lineHeight: 1.75,
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontFamily: '"Varela Round", sans-serif',
      fontWeight: 700,
      fontSize: '0.875rem', // 14px
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
    },
    // Body text - Roboto Regular as per brand guide
    body1: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 400,
      fontSize: '1rem', // 16px
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 400,
      fontSize: '0.875rem', // 14px
      lineHeight: 1.43,
      letterSpacing: '0.01071em',
    },
    // Button text
    button: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 500,
      fontSize: '0.875rem', // 14px
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'none',
    },
    // Caption text
    caption: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem', // 12px
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    // Overline text
    overline: {
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem', // 12px
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: '#00D41F',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 212, 31, 0.25)',
          '&:hover': {
            backgroundColor: '#00A018',
            boxShadow: '0 4px 12px rgba(0, 212, 31, 0.35)',
          },
        },
        containedSecondary: {
          backgroundColor: '#00D6FF',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#00A5CC',
          },
        },
        outlinedPrimary: {
          borderColor: '#00D41F',
          color: '#00D41F',
          '&:hover': {
            borderColor: '#00A018',
            backgroundColor: 'rgba(0, 212, 31, 0.08)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
          backgroundColor: '#00D41F',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#F0F5F7', // Harmonate Cloud background
          borderRight: '1px solid rgba(0, 212, 31, 0.12)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 212, 31, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(0, 212, 31, 0.25)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 212, 31, 0.08)',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'inherit',
          '& .MuiSvgIcon-root': {
            fontSize: 20,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        colorPrimary: {
          backgroundColor: '#00D41F',
          color: '#ffffff',
        },
        colorSecondary: {
          backgroundColor: '#00D6FF',
          color: '#ffffff',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

export default theme;